<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SeatOrdersAPI from 'dashboard/api/seatOrders';

const PRODUCT_KEY = 'storage';

const emit = defineEmits(['close']);

const { t } = useI18n();

// 存储档位：GB 为单位
const STORAGE_TIERS = [5, 10, 50, 100];
const pricePerUnit = ref(0);

const POPULAR_STORAGE = 10;

const STEP_SELECT = 'select';
const STEP_CONFIRM = 'confirm';

const dialogRef = ref(null);
const selectedStorage = ref(null);
const currentStep = ref(STEP_SELECT);
const isLoading = ref(false);
const error = ref(null);

// 支付页面相关状态
const showPayment = ref(false);
const paymentUrl = ref('');

const TOPUP_OPTIONS = computed(() =>
  STORAGE_TIERS.map(tier => ({
    storage: tier,
    price: tier * pricePerUnit.value,
  }))
);

const selectedOption = computed(() => {
  return TOPUP_OPTIONS.value.find(o => o.storage === selectedStorage.value);
});

const formattedPrice = computed(() => {
  if (!selectedOption.value) return '';
  return `¥${(selectedOption.value.price / 100).toFixed(2)}`;
});

const formattedStorage = computed(() => {
  if (!selectedOption.value) return '';
  return `${selectedOption.value.storage} GB`;
});

const dialogTitle = computed(() => {
  return currentStep.value === STEP_SELECT
    ? t('STORAGE_ORDER.MODAL_TITLE')
    : t('STORAGE_ORDER.CONFIRM.TITLE');
});

const dialogDescription = computed(() => {
  return currentStep.value === STEP_SELECT
    ? t('STORAGE_ORDER.MODAL_DESCRIPTION')
    : '';
});

const dialogWidth = computed(() => {
  return currentStep.value === STEP_SELECT ? '3xl' : 'md';
});

const handlePackageSelect = storage => {
  selectedStorage.value = storage;
};

const open = async () => {
  showPayment.value = false;
  paymentUrl.value = '';
  error.value = null;
  try {
    const response = await SeatOrdersAPI.getConfig(PRODUCT_KEY);
    pricePerUnit.value = response.data.price_per_unit || 0;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch storage price config:', err);
    pricePerUnit.value = 0;
  }
  selectedStorage.value = POPULAR_STORAGE;
  currentStep.value = STEP_SELECT;
  dialogRef.value?.open();
};

const close = () => {
  showPayment.value = false;
  paymentUrl.value = '';
  error.value = null;
  dialogRef.value?.close();
};

const handleClose = () => {
  emit('close');
};

const goToConfirmStep = () => {
  if (!selectedOption.value) return;
  currentStep.value = STEP_CONFIRM;
};

const goBackToSelectStep = () => {
  currentStep.value = STEP_SELECT;
};

const formatPrice = cents => {
  return `¥${(cents / 100).toFixed(2)}`;
};

// 确认购买
const handleConfirmPurchase = async () => {
  if (!selectedOption.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await SeatOrdersAPI.createOrder({
      quantity: selectedOption.value.storage,
      parentOrigin: window.location.origin,
      productKey: PRODUCT_KEY,
    });
    const { payment_url: url } = response.data;

    if (url) {
      paymentUrl.value = url;
      showPayment.value = true;
    }
  } catch (err) {
    error.value =
      err.response?.data?.error || '创建订单失败，请稍后重试';
  } finally {
    isLoading.value = false;
  }
};

// 监听支付完成消息
const handlePaymentMessage = event => {
  try {
    const expectedOrigin = paymentUrl.value
      ? new URL(paymentUrl.value).origin
      : null;
    if (!expectedOrigin || event.origin !== expectedOrigin) {
      return;
    }
  } catch {
    return;
  }

  if (event.data === 'payment_success' || event.data === 'payment_closed') {
    close();
    window.location.reload();
  }
};

onMounted(() => {
  window.addEventListener('message', handlePaymentMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handlePaymentMessage);
});

defineExpose({ open, close });
</script>

<template>
  <!-- 支付 iframe 模式 -->
  <Teleport v-if="showPayment" to="body">
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
      @click.self="close"
    >
      <div
        class="w-[550px] max-w-[95vw] h-[550px] max-h-[85vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden flex-shrink-0"
      >
        <!-- 标题栏 -->
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0"
        >
          <h3 class="text-lg font-medium text-slate-900">
            {{ t('STORAGE_ORDER.CONFIRM.TITLE') }}
          </h3>
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            @click="close"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>
        <!-- iframe 支付页面 -->
        <div class="flex-1 overflow-hidden relative">
          <iframe
            :src="paymentUrl"
            class="absolute inset-0 w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation"
          />
        </div>
      </div>
    </div>
  </Teleport>

  <!-- 选套餐 / 确认弹窗 -->
  <Dialog
    v-if="!showPayment"
    ref="dialogRef"
    :title="dialogTitle"
    :description="dialogDescription"
    :width="dialogWidth"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleClose"
  >
    <!-- Step 1: 选择套餐 -->
    <template v-if="currentStep === 'select'">
      <div class="grid grid-cols-2 gap-4">
        <label
          v-for="option in TOPUP_OPTIONS"
          v-show="option.price > 0"
          :key="option.storage"
          class="relative flex flex-col p-6 border-2 rounded-xl transition-all cursor-pointer bg-n-solid-1 hover:bg-n-solid-2"
          :class="[
            selectedStorage === option.storage
              ? 'border-woot-500'
              : 'border-n-weak hover:border-n-strong',
          ]"
        >
          <input
            type="radio"
            name="storage-package"
            :value="option.storage"
            :checked="selectedStorage === option.storage"
            class="sr-only"
            @change="handlePackageSelect(option.storage)"
          />

          <!-- Popular 标签 -->
          <span
            v-if="option.storage === POPULAR_STORAGE"
            class="absolute -top-3 left-4 px-3 py-1 text-xs font-medium rounded bg-woot-500 text-white"
          >
            {{ t('STORAGE_ORDER.POPULAR') }}
          </span>

          <!-- 选中勾选 -->
          <div
            v-if="selectedStorage === option.storage"
            class="absolute top-4 right-4 flex items-center justify-center w-6 h-6 rounded-full bg-woot-500"
          >
            <svg
              class="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <!-- 存储大小 -->
          <span class="text-3xl font-normal text-n-slate-12 mb-2 tracking-tighter">
            {{ option.storage }}
          </span>
          <span
            class="text-xs font-normal text-n-slate-11 uppercase tracking-tight mb-6"
          >
            {{ t('STORAGE_ORDER.UNIT') }}
          </span>

          <!-- 价格 -->
          <span class="text-2xl font-normal text-n-slate-12 tracking-tight">
            {{ formatPrice(option.price) }}
            <span class="text-sm text-n-slate-11 ml-0.5">{{
              t('STORAGE_ORDER.ONE_TIME')
            }}</span>
          </span>
        </label>
      </div>

      <div class="p-4 mt-6 rounded-lg bg-n-solid-2 border border-n-weak">
        <p class="text-sm text-n-slate-11">
          <span class="font-semibold text-n-slate-12">{{
            t('STORAGE_ORDER.NOTE_TITLE')
          }}</span>
          {{ t('STORAGE_ORDER.NOTE_DESCRIPTION') }}
        </p>
      </div>
    </template>

    <!-- Step 2: 确认购买 -->
    <template v-else>
      <div class="flex flex-col gap-4">
        <p class="text-sm text-n-slate-11">
          {{
            t('STORAGE_ORDER.CONFIRM.DESCRIPTION', {
              storage: formattedStorage,
              amount: formattedPrice,
            })
          }}
        </p>
        <div v-if="error" class="text-sm text-red-500">
          {{ error }}
        </div>
      </div>
    </template>

    <template #footer>
      <!-- Step 1 Footer -->
      <div
        v-if="currentStep === 'select'"
        class="flex items-center justify-between w-full gap-3"
      >
        <Button
          variant="faded"
          color="slate"
          :label="t('STORAGE_ORDER.CANCEL')"
          class="w-full"
          @click="close"
        />
        <Button
          color="blue"
          :label="t('STORAGE_ORDER.NEXT')"
          class="w-full"
          :disabled="!selectedStorage"
          @click="goToConfirmStep"
        />
      </div>

      <!-- Step 2 Footer -->
      <div v-else class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('STORAGE_ORDER.CONFIRM.GO_BACK')"
          class="w-full"
          :disabled="isLoading"
          @click="goBackToSelectStep"
        />
        <Button
          color="blue"
          :label="t('STORAGE_ORDER.CONFIRM.CONFIRM_PURCHASE')"
          class="w-full"
          :disabled="isLoading"
          @click="handleConfirmPurchase"
        />
      </div>
    </template>
  </Dialog>
</template>
